import {
  usePhoneticLibraryPersonalQuery,
  usePhoneticLibraryAllQuery,
} from '@replacements/graphql/operation.generated';
import { WatchQueryFetchPolicy } from '@apollo/client';
import {
  Actions,
  Subjects,
  useUserAbilitiesContext,
  useUserContext,
} from 'context/user/UserContext';
import { EnhancedPhoneticReplacement } from 'store/ReplacementsSlice';
import useEvaluateStudioProjectVersionFeatures from 'components/Studio/useEvaluateStudioProjectVersionFeatures';
import { StudioProjectVersion } from '../../../../server/graphql/__generated.types';

interface UseReplacementsLoadOptions {
  projectStudioVersion?: StudioProjectVersion;
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
}
/*
 * Fetch replacements library on user depending on their permissions
 */
const useReplacementsLoad = (options?: UseReplacementsLoadOptions) => {
  const {
    projectStudioVersion,
    fetchPolicy = 'cache-first',
    skip = false,
  } = options ?? {};
  const ability = useUserAbilitiesContext();
  const { data: userData } = useUserContext();
  const canReadTeamReplacementsLegacy = ability.can(
    Actions.UseFeature,
    Subjects.SharedLibrary
  );
  const canReadTeamReplacementsRbac = ability.can(
    Actions.Read,
    Subjects.TeamReplacements
  );
  const canReadTeamReplacements = userData?.user?.isRBACEnabled
    ? canReadTeamReplacementsRbac
    : canReadTeamReplacementsLegacy;

  const projectVersionFeatures = useEvaluateStudioProjectVersionFeatures({
    studioVersion: projectStudioVersion,
  });

  // Need to skip the personal library query for team users otherwise this query
  // will overwrite the team library replacements cache with an empty library
  const { data: individualUserLibraryData, loading } =
    usePhoneticLibraryPersonalQuery({
      skip:
        skip ||
        canReadTeamReplacements ||
        projectVersionFeatures.replacementsDisabled,
      fetchPolicy,
    });
  const { data: allLibraryData } = usePhoneticLibraryAllQuery({
    skip:
      skip ||
      !canReadTeamReplacements ||
      projectVersionFeatures.replacementsDisabled,
    fetchPolicy,
  });

  const individualUserLibrary =
    individualUserLibraryData?.user?.phoneticLibrary?.replacements_V2
      ?.personal ?? [];
  const teamUserLibrary = [
    ...(allLibraryData?.user?.phoneticLibrary?.replacements_V2?.team ?? []),
    ...(allLibraryData?.user?.phoneticLibrary?.replacements_V2?.personal ?? []),
  ];

  return {
    replacementsLibrary: [
      ...individualUserLibrary,
      ...teamUserLibrary,
    ] as EnhancedPhoneticReplacement[],
    loading,
  };
};

export default useReplacementsLoad;
